import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Users,
  Calendar,
  ClipboardCheck,
  Shield,
  FileText,
  Plus,
  Download,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowRight,
  Zap,
  Award,
  Bell,
  HelpCircle,
  XCircle,
  Crown,
  Sparkles,
  CreditCard,
  Archive,
  Settings,
  Building2,
  Rocket,
  Handshake,
  TrendingUp,
  UserCheck,
  UserPlus,
  ArrowUpCircle,
  Printer,
  Wallet,
  Lock,
  Euro,
  Receipt,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from '../components/ui/progress';
import { useDashboardConfig } from '../hooks/useFilters';
import DashboardConfigDialog from '../components/DashboardConfigDialog';
import { canAccessFeature, PLAN_HIERARCHY } from '../config/planFeatures';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatDateIT } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Plan configuration for display
const PLAN_DISPLAY = {
  free: { name: 'Free', icon: Users, color: 'slate', price: 'Gratuito' },
  premium: { name: 'Premium', icon: Sparkles, color: 'blue', price: '€9,99/mese' },
  governance: { name: 'Governance', icon: Building2, color: 'indigo', price: '€14,99/mese' },
  pro: { name: 'Pro', icon: Crown, color: 'amber', price: '€24,99/mese' },
  enterprise: { name: 'Enterprise', icon: Rocket, color: 'purple', price: '€49,99/mese' },
  convenzioni: { name: 'Convenzioni', icon: Handshake, color: 'emerald', price: 'Custom' }
};

// Enhanced Plan Widget Component with new counts
function PlanWidget({ planCounts, navigate }) {
  if (!planCounts) return null;
  
  const plan = planCounts.plan || 'free';
  const planInfo = PLAN_DISPLAY[plan] || PLAN_DISPLAY.free;
  const IconComponent = planInfo.icon;
  
  const counts = planCounts.counts || {};
  const limits = planCounts.limits || {};
  const exceeded = planCounts.exceeded || {};
  
  const isUnlimited = limits.max_volontari_attivi === -1;
  
  const getProgressColor = (isExceeded, current, limit) => {
    if (limit === -1) return '[&>div]:bg-green-500';
    if (isExceeded) return '[&>div]:bg-red-500';
    const percentage = (current / limit) * 100;
    if (percentage >= 90) return '[&>div]:bg-red-500';
    if (percentage >= 70) return '[&>div]:bg-amber-500';
    return '[&>div]:bg-green-500';
  };
  
  const getGradient = () => {
    if (exceeded.any) return 'from-amber-50 to-orange-50 border-amber-300';
    const colorMap = {
      slate: 'from-slate-50 to-gray-50 border-slate-200',
      blue: 'from-blue-50 to-indigo-50 border-blue-200',
      indigo: 'from-indigo-50 to-purple-50 border-indigo-200',
      amber: 'from-amber-50 to-yellow-50 border-amber-200',
      purple: 'from-purple-50 to-pink-50 border-purple-200',
      emerald: 'from-emerald-50 to-teal-50 border-emerald-200'
    };
    return colorMap[planInfo.color] || colorMap.slate;
  };
  
  const getBadgeColor = () => {
    const colorMap = {
      slate: 'bg-slate-100 text-slate-700',
      blue: 'bg-blue-100 text-blue-700',
      indigo: 'bg-indigo-100 text-indigo-700',
      amber: 'bg-amber-100 text-amber-700',
      purple: 'bg-purple-100 text-purple-700',
      emerald: 'bg-emerald-100 text-emerald-700'
    };
    return colorMap[planInfo.color] || colorMap.slate;
  };
  
  const getIconBg = () => {
    if (exceeded.any) return 'bg-amber-500';
    const colorMap = {
      slate: 'bg-slate-500',
      blue: 'bg-blue-500',
      indigo: 'bg-indigo-500',
      amber: 'bg-amber-500',
      purple: 'bg-purple-500',
      emerald: 'bg-emerald-500'
    };
    return colorMap[planInfo.color] || colorMap.slate;
  };
  
  return (
    <Card className={`bg-gradient-to-r ${getGradient()} border-2`} data-testid="plan-widget">
      <CardContent className="pt-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Plan Info */}
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl ${getIconBg()}`}>
              {exceeded.any ? (
                <AlertTriangle className="w-6 h-6 text-white" />
              ) : (
                <IconComponent className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-lg text-slate-800">Piano {planInfo.name}</span>
                <Badge className={getBadgeColor()}>
                  {planInfo.price}
                </Badge>
              </div>
              {planCounts.in_grace_period && planCounts.grace_until && (
                <p className="text-xs text-orange-600 mt-1 font-medium">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  Periodo di tolleranza fino al {planCounts.grace_until}
                </p>
              )}
            </div>
          </div>
          
          {/* Usage Stats - NEW: volontari_attivi + soggetti_unici */}
          {!isUnlimited && (
            <div className="flex flex-wrap gap-4 lg:gap-6">
              {/* Volontari Attivi */}
              <div className="min-w-[130px]">
                <div className="flex justify-between text-xs text-slate-600 mb-1.5">
                  <span className="flex items-center gap-1">
                    <UserCheck className="w-3 h-3" />
                    Volontari Attivi
                  </span>
                  <span className={exceeded.volontari ? 'text-red-600 font-bold' : 'font-medium'}>
                    {counts.volontari_attivi || 0}/{limits.max_volontari_attivi}
                  </span>
                </div>
                <Progress 
                  value={Math.min(((counts.volontari_attivi || 0) / limits.max_volontari_attivi) * 100, 100)}
                  className={`h-2 ${getProgressColor(exceeded.volontari, counts.volontari_attivi, limits.max_volontari_attivi)}`}
                />
              </div>
              
              {/* Soggetti Unici */}
              <div className="min-w-[130px]">
                <div className="flex justify-between text-xs text-slate-600 mb-1.5">
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    Soggetti Unici
                  </span>
                  <span className={exceeded.soggetti ? 'text-red-600 font-bold' : 'font-medium'}>
                    {counts.soggetti_unici || 0}/{limits.max_soggetti_unici}
                  </span>
                </div>
                <Progress 
                  value={Math.min(((counts.soggetti_unici || 0) / limits.max_soggetti_unici) * 100, 100)}
                  className={`h-2 ${getProgressColor(exceeded.soggetti, counts.soggetti_unici, limits.max_soggetti_unici)}`}
                />
              </div>
              
              {/* Eventi */}
              <div className="min-w-[100px]">
                <div className="flex justify-between text-xs text-slate-600 mb-1.5">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Eventi
                  </span>
                  <span className={exceeded.eventi ? 'text-red-600 font-bold' : 'font-medium'}>
                    {counts.eventi || 0}/{limits.max_eventi === -1 ? '∞' : limits.max_eventi}
                  </span>
                </div>
                {limits.max_eventi !== -1 && (
                  <Progress 
                    value={Math.min(((counts.eventi || 0) / limits.max_eventi) * 100, 100)}
                    className={`h-2 ${getProgressColor(exceeded.eventi, counts.eventi, limits.max_eventi)}`}
                  />
                )}
              </div>
            </div>
          )}
          
          {/* Unlimited Badge or Upgrade Alert */}
          <div className="flex flex-col gap-2 items-end">
            {isUnlimited ? (
              <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600">
                <Rocket className="w-3 h-3 mr-1" />
                Risorse Illimitate
              </Badge>
            ) : exceeded.any ? (
              <div className="flex flex-col items-end gap-2">
                <div className="text-right">
                  <p className="text-xs text-amber-700 font-medium">
                    Limite superato! Upgrade consigliato:
                  </p>
                  <p className="text-sm font-bold text-amber-800">
                    {planCounts.suggested_plan_name} - €{planCounts.suggested_price}/mese
                  </p>
                </div>
                <Button 
                  onClick={() => navigate('/pricing')}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white animate-pulse"
                  size="sm"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Effettua Upgrade
                </Button>
              </div>
            ) : (
              <Button 
                onClick={() => navigate('/pricing')}
                variant="outline"
                size="sm"
                className="border-slate-300"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Vedi Piani
              </Button>
            )}
          </div>
        </div>
        
        {/* Alert Banner if limit exceeded */}
        {exceeded.any && (
          <div className="mt-4 p-3 bg-amber-100 border border-amber-300 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">
                  {exceeded.volontari && exceeded.soggetti 
                    ? 'Hai superato i limiti di volontari attivi e soggetti unici.'
                    : exceeded.volontari 
                      ? 'Hai superato il limite di volontari attivi.'
                      : exceeded.soggetti
                        ? 'Hai superato il limite di soggetti unici gestiti.'
                        : exceeded.eventi
                          ? 'Hai superato il limite di eventi annuali.'
                          : 'Hai superato uno dei limiti del piano.'}
                </p>
                {planCounts.in_grace_period ? (
                  <p className="mt-1 text-amber-700">
                    Hai un periodo di tolleranza di 3 mesi per effettuare l'upgrade senza perdere funzionalità.
                  </p>
                ) : (
                  <p className="mt-1 text-red-700 font-medium">
                    Il periodo di tolleranza è scaduto. Effettua l'upgrade per continuare ad utilizzare tutte le funzionalità.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [planCounts, setPlanCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  
  // Stati per modali Quote
  const [showQuoteDaIncassare, setShowQuoteDaIncassare] = useState(false);
  const [showQuoteIncassate, setShowQuoteIncassate] = useState(false);
  const [quoteDaIncassare, setQuoteDaIncassare] = useState([]);
  const [quoteIncassate, setQuoteIncassate] = useState([]);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [selectedQuotaPagamento, setSelectedQuotaPagamento] = useState(null);
  const [pagamentoData, setPagamentoData] = useState({
    metodo_pagamento: 'contanti',
    data_pagamento: new Date().toISOString().split('T')[0],
    ricevuta_numero: ''
  });
  const [submittingPagamento, setSubmittingPagamento] = useState(false);
  
  // Dashboard configuration hook
  const { 
    config, 
    toggleWidget, 
    reorderWidgets, 
    resetConfig, 
    enabledWidgets 
  } = useDashboardConfig();

  // Helper function to check if a widget is enabled
  const isWidgetEnabled = (widgetId) => enabledWidgets.some(w => w.id === widgetId);

  useEffect(() => {
    fetchStats();
    fetchSubscription();
    fetchPlanCounts();
  }, []);

  const fetchPlanCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API_URL}/api/subscription/counts`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setPlanCounts(data);
      }
    } catch (error) {
      console.error('Error fetching plan counts:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API_URL}/api/subscription`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchStats = async () => {
    try {
      // Try enhanced stats first, fallback to basic
      let response = await fetch(`${API_URL}/api/dashboard/stats/enhanced`, {
        credentials: 'include'
      });
      if (!response.ok) {
        response = await fetch(`${API_URL}/api/dashboard/stats`, {
          credentials: 'include'
        });
      }
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Errore nel caricamento delle statistiche');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadRegistro = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reports/registro-volontari`, {
        credentials: 'include'
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `registro_volontari_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Registro Volontari PDF scaricato con successo');
      } else {
        toast.error('Errore durante il download');
      }
    } catch (error) {
      toast.error('Errore durante il download');
    }
  };

  const handleDownloadRegistroSoci = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reports/registro-soci-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `registro_soci_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Registro Soci PDF scaricato con successo');
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Errore durante il download del Registro Soci');
      }
    } catch (error) {
      toast.error('Errore durante il download');
    }
  };

  // Funzioni per Quote Associative
  const fetchQuoteDaIncassare = useCallback(async () => {
    setLoadingQuote(true);
    try {
      const token = localStorage.getItem('token');
      const annoCorrente = new Date().getFullYear();
      const response = await fetch(
        `${API_URL}/api/soci/quote/lista?anno=${annoCorrente}&stato=da_pagare`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setQuoteDaIncassare(data.quote || []);
      }
    } catch (error) {
      console.error('Errore caricamento quote:', error);
      toast.error('Errore nel caricamento delle quote');
    } finally {
      setLoadingQuote(false);
    }
  }, []);

  const fetchQuoteIncassate = useCallback(async () => {
    setLoadingQuote(true);
    try {
      const token = localStorage.getItem('token');
      const annoCorrente = new Date().getFullYear();
      const response = await fetch(
        `${API_URL}/api/soci/quote/lista?anno=${annoCorrente}&stato=pagata`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setQuoteIncassate(data.quote || []);
      }
    } catch (error) {
      console.error('Errore caricamento quote:', error);
      toast.error('Errore nel caricamento delle quote');
    } finally {
      setLoadingQuote(false);
    }
  }, []);

  const handleOpenQuoteDaIncassare = () => {
    setShowQuoteDaIncassare(true);
    fetchQuoteDaIncassare();
  };

  const handleOpenQuoteIncassate = () => {
    setShowQuoteIncassate(true);
    fetchQuoteIncassate();
  };

  const handleRegistraPagamento = async () => {
    if (!selectedQuotaPagamento) return;
    
    setSubmittingPagamento(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('metodo_pagamento', pagamentoData.metodo_pagamento);
      if (pagamentoData.data_pagamento) params.append('data_pagamento', pagamentoData.data_pagamento);
      if (pagamentoData.ricevuta_numero) params.append('ricevuta_numero', pagamentoData.ricevuta_numero);
      
      const response = await fetch(
        `${API_URL}/api/soci/quote/${selectedQuotaPagamento.quota_id}/paga?${params}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        toast.success(`Pagamento registrato per ${selectedQuotaPagamento.nome_socio}`);
        setSelectedQuotaPagamento(null);
        setPagamentoData({
          metodo_pagamento: 'contanti',
          data_pagamento: new Date().toISOString().split('T')[0],
          ricevuta_numero: ''
        });
        fetchQuoteDaIncassare(); // Aggiorna lista
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore nella registrazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSubmittingPagamento(false);
    }
  };

  // Get user's plan for feature gating
  const userPlan = user?.plan || 'free';
  const isTopPlan = userPlan === 'enterprise' || userPlan === 'convenzioni';
  
  // Check if user can access soci (Premium+)
  const canAccessSoci = canAccessFeature(userPlan, 'premium');
  // Check if user can access rendicontazione (Governance+)  
  const canAccessRendicontazione = canAccessFeature(userPlan, 'governance');

  // Definisco tutte le azioni rapide con i relativi piani richiesti
  const allQuickActions = [
    { 
      label: 'Aggiungi Volontario', 
      icon: Users, 
      path: '/volontari/nuovo', 
      color: 'bg-[#0055BB]',
      requiredPlan: 'free' // Disponibile per tutti
    },
    { 
      label: 'Crea Evento', 
      icon: Calendar, 
      path: '/eventi/nuovo', 
      color: 'bg-[#008A55]',
      requiredPlan: 'free' // Disponibile per tutti
    },
    { 
      label: 'Check-in Rapido', 
      icon: Zap, 
      path: '/checkin', 
      color: 'bg-amber-500',
      requiredPlan: 'free' // Disponibile per tutti
    },
    { 
      label: 'Aggiungi Socio', 
      icon: UserPlus, 
      path: '/soci/nuovo', 
      color: 'bg-indigo-500',
      requiredPlan: 'premium' // Solo Premium+
    },
    // NUOVI BOTTONI QUOTE
    { 
      label: 'Quote da Incassare', 
      icon: Clock, 
      onClick: handleOpenQuoteDaIncassare, 
      color: 'bg-yellow-500',
      requiredPlan: 'premium' // Quote associative = Premium+
    },
    { 
      label: 'Quote Incassate', 
      icon: Receipt, 
      onClick: handleOpenQuoteIncassate, 
      color: 'bg-green-600',
      requiredPlan: 'premium' // Quote associative = Premium+
    },
    { 
      label: 'Registra Movimento Contabile', 
      icon: Wallet, 
      path: '/contabilita',
      color: 'bg-emerald-500',
      requiredPlan: 'governance' // Solo Governance+
    },
    { 
      label: 'Esporta Registro Volontari', 
      icon: Download, 
      onClick: handleDownloadRegistro, 
      color: 'bg-slate-600',
      requiredPlan: 'free' // Disponibile per tutti
    },
    { 
      label: 'Esporta Registro Soci', 
      icon: Users, 
      onClick: handleDownloadRegistroSoci,
      color: 'bg-indigo-600',
      requiredPlan: 'premium' // Solo Premium+ (Soci)
    },
  ];

  // Gerarchia piani per confronto
  const planHierarchy = ['free', 'premium', 'governance', 'pro', 'enterprise', 'convenzioni'];
  
  const canAccessAction = (requiredPlan) => {
    const userLevel = planHierarchy.indexOf(userPlan || 'free');
    const requiredLevel = planHierarchy.indexOf(requiredPlan);
    return userLevel >= requiredLevel;
  };

  // Preparo le azioni con lo stato di accesso
  const quickActions = allQuickActions.map(action => ({
    ...action,
    locked: !canAccessAction(action.requiredPlan)
  }));

  // Aggiungo il pulsante Upgrade solo se non è al top
  if (!isTopPlan) {
    quickActions.push({ 
      label: 'Fai Upgrade', 
      icon: ArrowUpCircle, 
      path: '/pricing', 
      color: 'bg-gradient-to-r from-purple-500 to-pink-500',
      locked: false
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const hasAlerts = (stats?.coperture?.in_scadenza > 0 || 
                    stats?.coperture?.volontari_senza_copertura > 0 || 
                    stats?.volontari?.documenti_mancanti > 0);

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="page-header flex items-start justify-between">
        <div>
          <h1 className="page-title">Ciao, {user?.name?.split(' ')[0] || 'Utente'}!</h1>
          <p className="page-subtitle">Ecco una panoramica della tua associazione</p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setShowConfig(true)}
          className="hidden sm:flex"
        >
          <Settings className="h-4 w-4 mr-1" />
          Personalizza
        </Button>
      </div>

      {/* Dashboard Config Dialog */}
      <DashboardConfigDialog 
        open={showConfig}
        onOpenChange={setShowConfig}
        config={config}
        toggleWidget={toggleWidget}
        reorderWidgets={reorderWidgets}
        resetConfig={resetConfig}
      />

      {/* Alert Banner */}
      {hasAlerts && isWidgetEnabled('alerts') && (
        <div className="alert-warning flex items-start gap-3" data-testid="alert-banner">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Attenzione</p>
            <ul className="text-sm mt-1 space-y-1">
              {stats?.coperture?.in_scadenza > 0 && (
                <li>• {stats.coperture.in_scadenza} coperture in scadenza nei prossimi 30 giorni</li>
              )}
              {stats?.coperture?.volontari_senza_copertura > 0 && (
                <li>• {stats.coperture.volontari_senza_copertura} volontari senza copertura assicurativa</li>
              )}
              {stats?.volontari?.documenti_mancanti > 0 && (
                <li>• {stats.volontari.documenti_mancanti} volontari con documenti mancanti</li>
              )}
            </ul>
            <Button 
              variant="link" 
              className="p-0 h-auto text-yellow-800 hover:text-yellow-900"
              onClick={() => navigate('/coperture')}
            >
              Verifica ora <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Backup Reminder - shows weekly */}
      {(() => {
        const lastBackupReminder = localStorage.getItem('lastBackupReminder');
        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const shouldShowReminder = !lastBackupReminder || new Date(lastBackupReminder) < weekAgo;
        
        if (shouldShowReminder) {
          return (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3" data-testid="backup-reminder">
              <Archive className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-blue-800">Promemoria Backup</p>
                <p className="text-sm text-blue-700 mt-1">
                  Per maggiore sicurezza ti consigliamo di scaricare periodicamente una copia dei documenti caricati su Assietica Gest.
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Button 
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => navigate('/documenti')}
                  >
                    <Archive className="w-4 h-4 mr-1" />
                    Vai ai Documenti
                  </Button>
                  <Button 
                    size="sm"
                    variant="ghost"
                    className="text-blue-600"
                    onClick={() => {
                      localStorage.setItem('lastBackupReminder', new Date().toISOString());
                      window.location.reload();
                    }}
                  >
                    Ricordamelo più tardi
                  </Button>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Stats Grid */}
      {isWidgetEnabled('stats') && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stats-card card-hover cursor-pointer" onClick={() => navigate('/volontari')} data-testid="stat-volontari">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stats-label">Volontari Attivi</p>
                <p className="stats-value">{stats?.volontari?.attivi || 0}</p>
                {stats?.volontari?.da_completare > 0 && (
                  <Badge variant="outline" className="mt-2 bg-orange-50 text-orange-700 border-orange-200">
                    {stats.volontari.da_completare} da completare
                  </Badge>
                )}
              </div>
              <div className="w-12 h-12 rounded-full bg-[#0055BB]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#0055BB]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card card-hover cursor-pointer" onClick={() => navigate('/soci')} data-testid="stat-soci">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stats-label">Soci</p>
                <p className="stats-value">{stats?.soci?.totali || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card card-hover cursor-pointer" onClick={() => navigate('/eventi')} data-testid="stat-eventi">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stats-label">Eventi Programmati</p>
                <p className="stats-value">{stats?.eventi?.programmati || 0}</p>
                <p className="text-sm text-slate-500 mt-1">{stats?.eventi?.conclusi || 0} conclusi</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#008A55]/10 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#008A55]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card card-hover cursor-pointer" onClick={() => navigate('/coperture')} data-testid="stat-coperture">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stats-label">Coperture in Scadenza</p>
                <p className="stats-value">{stats?.coperture?.in_scadenza || 0}</p>
                <p className="text-sm text-slate-500 mt-1">nei prossimi 30 giorni</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Prossimo Evento & Disponibilità */}
      {isWidgetEnabled('recent-events') && stats?.prossimo_evento && (
        <Card data-testid="prossimo-evento">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#0055BB]" />
              Prossimo Evento: {stats.prossimo_evento.titolo}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1">
                <p className="text-slate-600">
                  <strong>Data:</strong> {stats.prossimo_evento.data} {stats.prossimo_evento.orario_inizio && `alle ${stats.prossimo_evento.orario_inizio}`}
                </p>
                {stats.prossimo_evento.luogo && (
                  <p className="text-slate-600"><strong>Luogo:</strong> {stats.prossimo_evento.luogo}</p>
                )}
              </div>
              
              {stats.disponibilita_prossimo && (
                <div className="flex gap-3">
                  <div className="text-center px-4 py-2 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle className="w-5 h-5 mx-auto text-green-600 mb-1" />
                    <p className="text-lg font-bold text-green-700">{stats.disponibilita_prossimo.confermati}</p>
                    <p className="text-xs text-green-600">Confermati</p>
                  </div>
                  <div className="text-center px-4 py-2 bg-yellow-50 rounded-lg border border-yellow-200">
                    <HelpCircle className="w-5 h-5 mx-auto text-yellow-600 mb-1" />
                    <p className="text-lg font-bold text-yellow-700">{stats.disponibilita_prossimo.forse}</p>
                    <p className="text-xs text-yellow-600">Forse</p>
                  </div>
                  <div className="text-center px-4 py-2 bg-slate-50 rounded-lg border border-slate-200">
                    <Clock className="w-5 h-5 mx-auto text-slate-500 mb-1" />
                    <p className="text-lg font-bold text-slate-600">{stats.disponibilita_prossimo.in_attesa}</p>
                    <p className="text-xs text-slate-500">In Attesa</p>
                  </div>
                  <div className="text-center px-4 py-2 bg-red-50 rounded-lg border border-red-200">
                    <XCircle className="w-5 h-5 mx-auto text-red-600 mb-1" />
                    <p className="text-lg font-bold text-red-700">{stats.disponibilita_prossimo.non_disponibili}</p>
                    <p className="text-xs text-red-600">Non Possono</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={() => navigate(`/eventi/${stats.prossimo_evento.evento_id}`)} variant="outline">
                Dettagli Evento
              </Button>
              <Button onClick={() => navigate(`/checkin?evento=${stats.prossimo_evento.evento_id}`)} className="btn-success">
                <Zap className="w-4 h-4 mr-2" />
                Check-in
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      {isWidgetEnabled('quick-actions') && (
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Azioni Rapide</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <button
              key={index}
              className={`quick-action ${action.locked ? 'opacity-60 cursor-not-allowed' : ''}`}
              onClick={() => {
                if (action.locked) {
                  toast.info(`Questa funzionalità richiede il piano ${action.requiredPlan?.charAt(0).toUpperCase() + action.requiredPlan?.slice(1) || 'a pagamento'}`, {
                    action: {
                      label: 'Upgrade',
                      onClick: () => navigate('/pricing')
                    }
                  });
                  return;
                }
                if (action.onClick) {
                  action.onClick();
                } else {
                  navigate(action.path);
                }
              }}
              data-testid={`quick-action-${index}`}
            >
              <div className={`w-12 h-12 rounded-full ${action.color} flex items-center justify-center mb-3 relative`}>
                <action.icon className="w-6 h-6 text-white" />
                {action.locked && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-slate-700 rounded-full flex items-center justify-center">
                    <Lock className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
              <span className="quick-action-label">{action.label}</span>
              {action.locked && (
                <span className="text-xs text-slate-400 mt-1">
                  {action.requiredPlan?.charAt(0).toUpperCase() + action.requiredPlan?.slice(1)}+
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      )}

      {/* Plan Widget - Stato del Piano */}
      {isWidgetEnabled('plan') && planCounts && (
        <PlanWidget planCounts={planCounts} navigate={navigate} />
      )}

      {/* Recent Badges & Activity */}
      {isWidgetEnabled('recent-activities') && (
      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent Badges */}
        {stats?.recent_badges?.length > 0 && (
          <Card data-testid="recent-badges">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Badge Recenti
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.recent_badges.map((badge, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                    <Award className="w-8 h-8 text-amber-500" />
                    <div>
                      <p className="font-medium text-amber-800">{badge.nome}</p>
                      <p className="text-sm text-amber-600">
                        {badge.volontario_nome} • {badge.data_assegnazione}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="ghost" className="w-full mt-4" onClick={() => navigate('/ore-volontari')}>
                Vedi tutti i badge <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Recent Activity */}
        {stats?.recent_logs?.length > 0 && (
          <Card data-testid="recent-activity">
            <CardHeader>
              <CardTitle className="text-lg">Attività Recenti</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.recent_logs.map((log, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      log.esito === 'successo' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {log.esito === 'successo' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">
                        {log.operazione.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {log.dettagli || `${log.sezione} • ${log.user_name}`}
                      </p>
                    </div>
                    <span className="timestamp">
                      {new Date(log.timestamp).toLocaleString('it-IT', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </span>
                  </div>
                ))}
              </div>
              <Button variant="ghost" className="w-full mt-4" onClick={() => navigate('/logs')}>
                Visualizza tutti i log <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      )}

      {/* Disclaimer */}
      <div className="disclaimer-banner">
        <p>
          <strong>Nota:</strong> Verificare sempre con il proprio consulente legale/amministrativo la conformità delle procedure adottate dall'ente. 
          Le configurazioni di conservazione e validazione documentale devono essere adeguate alle specifiche esigenze normative dell'ente.
        </p>
      </div>

      {/* ============= MODALE QUOTE DA INCASSARE ============= */}
      <Dialog open={showQuoteDaIncassare} onOpenChange={setShowQuoteDaIncassare}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              Quote da Incassare - {new Date().getFullYear()}
            </DialogTitle>
            <DialogDescription>
              Soci con quota associativa ancora da pagare per l'anno corrente
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {loadingQuote ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : quoteDaIncassare.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
                <p className="font-medium">Nessuna quota da incassare!</p>
                <p className="text-sm">Tutti i soci sono in regola con i pagamenti.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Riepilogo totale */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-yellow-800 font-medium">Totale da incassare:</span>
                    <span className="text-xl font-bold text-yellow-700">
                      €{quoteDaIncassare.reduce((sum, q) => sum + (q.importo || 0), 0).toFixed(2)}
                    </span>
                  </div>
                  <p className="text-sm text-yellow-600 mt-1">{quoteDaIncassare.length} quote in attesa</p>
                </div>
                
                {/* Lista quote */}
                {quoteDaIncassare.map((quota) => (
                  <div 
                    key={quota.quota_id} 
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{quota.nome_socio}</p>
                        <div className="flex gap-4 text-sm text-gray-500 mt-1">
                          <span>Anno: {quota.anno}</span>
                          <span>Scadenza: {formatDateIT(quota.data_scadenza)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-gray-800">€{quota.importo?.toFixed(2)}</span>
                        <Button 
                          size="sm" 
                          onClick={() => setSelectedQuotaPagamento(quota)}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`paga-rapido-${quota.quota_id}`}
                        >
                          <Euro className="h-4 w-4 mr-1" />
                          Paga
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4 mt-2 flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => navigate('/quote')}
              className="gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Vai a Quote Associative
            </Button>
            <Button variant="ghost" onClick={() => setShowQuoteDaIncassare(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============= MODALE QUOTE INCASSATE ============= */}
      <Dialog open={showQuoteIncassate} onOpenChange={setShowQuoteIncassate}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-green-600" />
              Quote Incassate - {new Date().getFullYear()}
            </DialogTitle>
            <DialogDescription>
              Soci che hanno già pagato la quota associativa per l'anno corrente
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {loadingQuote ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : quoteIncassate.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">Nessuna quota incassata</p>
                <p className="text-sm">Non ci sono ancora pagamenti registrati per quest'anno.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Riepilogo totale */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-green-800 font-medium">Totale incassato:</span>
                    <span className="text-xl font-bold text-green-700">
                      €{quoteIncassate.reduce((sum, q) => sum + (q.importo || 0), 0).toFixed(2)}
                    </span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">{quoteIncassate.length} quote pagate</p>
                </div>
                
                {/* Lista quote */}
                {quoteIncassate.map((quota) => (
                  <div 
                    key={quota.quota_id} 
                    className="border rounded-lg p-4 bg-green-50/30"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{quota.nome_socio}</p>
                        <div className="flex gap-4 text-sm text-gray-500 mt-1">
                          <span>Anno: {quota.anno}</span>
                          <span className="text-green-600">
                            Pagato il: {formatDateIT(quota.data_pagamento)}
                          </span>
                          {quota.ricevuta_numero && (
                            <span>Ricevuta: {quota.ricevuta_numero}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-green-700">€{quota.importo?.toFixed(2)}</span>
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4 mt-2 flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => navigate('/quote')}
              className="gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Vai a Quote Associative
            </Button>
            <Button variant="ghost" onClick={() => setShowQuoteIncassate(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============= MODALE REGISTRA PAGAMENTO RAPIDO ============= */}
      <Dialog 
        open={!!selectedQuotaPagamento} 
        onOpenChange={(open) => !open && setSelectedQuotaPagamento(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Euro className="h-5 w-5 text-green-600" />
              Registra Pagamento
            </DialogTitle>
            <DialogDescription>
              {selectedQuotaPagamento && (
                <>Quota {selectedQuotaPagamento.anno} - <strong>{selectedQuotaPagamento.nome_socio}</strong> - €{selectedQuotaPagamento.importo?.toFixed(2)}</>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Data Effettiva Pagamento</Label>
              <Input
                type="date"
                value={pagamentoData.data_pagamento}
                onChange={(e) => setPagamentoData({...pagamentoData, data_pagamento: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Metodo di Pagamento</Label>
              <Select 
                value={pagamentoData.metodo_pagamento} 
                onValueChange={(v) => setPagamentoData({...pagamentoData, metodo_pagamento: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contanti">Contanti</SelectItem>
                  <SelectItem value="bonifico">Bonifico Bancario</SelectItem>
                  <SelectItem value="carta">Carta di Credito</SelectItem>
                  <SelectItem value="assegno">Assegno</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Numero Ricevuta (opzionale)</Label>
              <Input
                value={pagamentoData.ricevuta_numero}
                onChange={(e) => setPagamentoData({...pagamentoData, ricevuta_numero: e.target.value})}
                placeholder="Es. RIC-2026-001"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedQuotaPagamento(null)}>
              Annulla
            </Button>
            <Button 
              onClick={handleRegistraPagamento} 
              disabled={submittingPagamento}
              className="bg-green-600 hover:bg-green-700"
            >
              {submittingPagamento ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Registrazione...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Conferma Pagamento
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
